import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Alert, Typography, Box, Chip,
} from '@mui/material';
import { apiService } from '../services/apiService';
import { websocketService } from '../services/websocketService';

const severityMap = { info: 'info', warning: 'warning', critical: 'error', error: 'error', success: 'success' };

// Categorise a system MOTD's scope based on the create-form target.
// Used purely for the dialog header chip so the user knows which audience
// the announcement was sent to (System / Organization / Project / Observatory).
const systemScopeLabel = (m) => {
  const t = m.target;
  if (t?.organization_ids?.length > 0) return 'Organization';
  if (t?.observatory_ids?.length > 0) return 'Observatory';
  if (t?.project_ids?.length > 0) return 'Project';
  return 'System';
};

/**
 * On every mount (login lands users on Operations View or Admin Control
 * Center), fetch /v1/user/motd/pending and walk every unacknowledged
 * MOTD the user is targeted by — system-wide, organization-targeted,
 * project-targeted, and per-observatory ones. Acknowledging routes
 * through the right endpoint based on which scope the MOTD came from.
 */
const SystemMotdDialog = ({ onAllAcknowledged }) => {
  const [queue, setQueue] = useState([]); // [{ kind: 'system'|'observatory', motd, observatoryId? }]
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const fetchPending = useCallback(async ({ force = false } = {}) => {
    try {
      const result = await apiService.getPendingMotds();
      if (!result?.success) return;
      const data = result.data || {};
      const items = [];

      for (const m of data.system_motds || []) {
        items.push({ kind: 'system', motd: m });
      }
      for (const grp of data.observatories || []) {
        const obsId = grp.observatory?._id || grp.observatory?.id;
        for (const m of grp.motds || []) {
          items.push({ kind: 'observatory', motd: m, observatoryId: obsId, observatoryName: grp.observatory?.name });
        }
      }

      if (items.length > 0) {
        setQueue(items);
        setCurrentIndex(0);
        setOpen(true);
      } else if (force) {
        // No pending — caller asked us to refresh; close any stale state.
        setQueue([]);
        setOpen(false);
      }
    } catch (err) {
      console.error('Failed to fetch pending MOTDs:', err);
    }
  }, []);

  useEffect(() => {
    fetchPending();
  }, [fetchPending]);

  // Live MOTD broadcasts: when an admin posts a new system /
  // observatory MOTD, the server emits via WebSocket so every logged-in
  // user can pull /v1/user/motd/pending and see the acknowledge dialog
  // without re-logging.
  useEffect(() => {
    const handler = () => { fetchPending({ force: true }); };
    websocketService.on('motdReceived', handler);
    return () => websocketService.off('motdReceived', handler);
  }, [fetchPending]);

  const current = queue[currentIndex];

  const handleAcknowledge = async () => {
    if (!current) return;
    setLoading(true);
    try {
      if (current.kind === 'observatory') {
        await apiService.acknowledgeMotd(current.observatoryId, current.motd._id);
      } else {
        await apiService.acknowledgeSystemMotd(current.motd._id);
      }

      if (currentIndex + 1 < queue.length) {
        setCurrentIndex((prev) => prev + 1);
      } else {
        setOpen(false);
        onAllAcknowledged?.();
      }
    } catch (err) {
      console.error('Failed to acknowledge MOTD:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!open || !current) return null;

  const m = current.motd;
  const severity = severityMap[m.severity] || 'info';
  const scopeLabel = current.kind === 'observatory'
    ? `Observatory${current.observatoryName ? ` — ${current.observatoryName}` : ''}`
    : systemScopeLabel(m);

  return (
    <Dialog open={open} maxWidth="sm" fullWidth disableEscapeKeyDown>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        Announcement
        <Chip label={scopeLabel} size="small" variant="outlined" />
        <Chip label={(m.severity || 'info').toUpperCase()} color={severity} size="small" />
        {queue.length > 1 && (
          <Chip label={`${currentIndex + 1} of ${queue.length}`} size="small" variant="outlined" />
        )}
      </DialogTitle>
      <DialogContent>
        <Typography variant="h6" gutterBottom>{m.title}</Typography>
        <Alert severity={severity} sx={{ mb: 2 }}>
          {m.message}
        </Alert>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {m.created_by && (
            <Typography variant="caption" color="text.secondary">
              By: {m.created_by.first_name} {m.created_by.last_name}
            </Typography>
          )}
          {m.expires_at && (
            <Typography variant="caption" color="text.secondary">
              Expires: {new Date(m.expires_at).toLocaleString()}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleAcknowledge} variant="contained" disabled={loading}>
          {loading ? 'Acknowledging...' : 'I Acknowledge'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SystemMotdDialog;
